import { useEffect, useMemo, useState } from "react";
import { X, Pencil } from "lucide-react";

function slugify(input = "") {
  return input
    .toString()
    .trim()
    .toLowerCase()
    .replace(/['"]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

export default function EditCategoryModal({
  open,
  onClose,
  onUpdate,
  categories = [],
  category, // the selected category object
}) {
  const parentOptions = useMemo(() => {
    // prevent selecting itself as parent
    return categories.filter(
      (c) => c.parentId == null && c.id !== category?.id
    );
  }, [categories, category?.id]);

  const [form, setForm] = useState({
    name: "",
    slug: "",
    description: "",
    imgUrl: "",
    parentId: "",
  });

  const [autoSlug, setAutoSlug] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open || !category) return;
    setForm({
      name: category.name || "",
      slug: category.slug || "",
      description: category.description || "",
      imgUrl: category.img?.url || "",
      parentId: category.parentId ? String(category.parentId) : "",
    });
    setAutoSlug(false);
    setError("");
  }, [open, category]);

  function setField(name, value) {
    setForm((p) => ({ ...p, [name]: value }));
  }

  function handleNameChange(v) {
    setField("name", v);
    if (autoSlug) setField("slug", slugify(v));
  }

  function submit(e) {
    e.preventDefault();
    setError("");

    if (!category?.id) return setError("No category selected.");
    const name = form.name.trim();
    const slug = form.slug.trim();

    if (!name) return setError("Category name is required.");
    if (!slug) return setError("Slug is required.");

    const payload = {
      id: category.id,
      name,
      slug,
      description: form.description.trim() || null,
      parentId: form.parentId ? Number(form.parentId) : null,
      img: form.imgUrl.trim() ? { url: form.imgUrl.trim() } : null,
    };

    onUpdate?.(payload);
  }

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[100]">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="absolute inset-0 flex items-center justify-center p-4">
        <div className="w-full max-w-lg rounded-2xl bg-white shadow-xl ring-1 ring-slate-200">
          <div className="flex items-center justify-between border-b p-4">
            <div className="flex items-center gap-2">
              <Pencil className="h-5 w-5 text-slate-700" />
              <div className="font-semibold text-slate-900">Edit Category</div>
            </div>
            <button
              onClick={onClose}
              className="rounded-xl p-2 hover:bg-slate-100"
            >
              <X className="h-5 w-5 text-slate-600" />
            </button>
          </div>

          <form onSubmit={submit} className="p-4 space-y-4">
            {error && (
              <div className="rounded-xl bg-rose-50 px-3 py-2 text-sm text-rose-700">
                {error}
              </div>
            )}

            <div className="grid gap-3">
              <label className="text-sm text-slate-600">Name</label>
              <input
                value={form.name}
                onChange={(e) => handleNameChange(e.target.value)}
                className="rounded-xl border border-slate-200 px-3 py-2 outline-none focus:ring-2 focus:ring-slate-200"
              />
            </div>

            <div className="grid gap-2">
              <div className="flex items-center justify-between">
                <label className="text-sm text-slate-600">Slug</label>
                <label className="flex items-center gap-2 text-xs text-slate-500">
                  <input
                    type="checkbox"
                    checked={autoSlug}
                    onChange={(e) => setAutoSlug(e.target.checked)}
                  />
                  Auto-generate
                </label>
              </div>
              <input
                value={form.slug}
                onChange={(e) => {
                  setAutoSlug(false);
                  setField("slug", e.target.value);
                }}
                className="rounded-xl border border-slate-200 px-3 py-2 outline-none focus:ring-2 focus:ring-slate-200"
              />
            </div>

            <div className="grid gap-3">
              <label className="text-sm text-slate-600">Parent Category</label>
              <select
                value={form.parentId}
                onChange={(e) => setField("parentId", e.target.value)}
                className="rounded-xl border border-slate-200 px-3 py-2 outline-none focus:ring-2 focus:ring-slate-200"
              >
                <option value="">No parent (Top-level)</option>
                {parentOptions.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="grid gap-3">
              <label className="text-sm text-slate-600">
                Image URL (optional)
              </label>
              <input
                value={form.imgUrl}
                onChange={(e) => setField("imgUrl", e.target.value)}
                className="rounded-xl border border-slate-200 px-3 py-2 outline-none focus:ring-2 focus:ring-slate-200"
                placeholder="https://..."
              />
              {form.imgUrl?.trim() ? (
                <img
                  src={form.imgUrl.trim()}
                  alt="Preview"
                  className="h-20 w-20 rounded-xl object-cover ring-1 ring-slate-200"
                />
              ) : null}
            </div>

            <div className="grid gap-3">
              <label className="text-sm text-slate-600">
                Description (optional)
              </label>
              <textarea
                value={form.description}
                onChange={(e) => setField("description", e.target.value)}
                className="min-h-[90px] rounded-xl border border-slate-200 px-3 py-2 outline-none focus:ring-2 focus:ring-slate-200"
              />
            </div>

            <div className="flex items-center justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={onClose}
                className="rounded-xl bg-slate-100 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-200"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="rounded-xl bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800"
              >
                Save Changes
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
